"use client"

import { Button, LoadingButton } from "@/components/ui/button"
import { NAV_LIST } from "@/constants/router"

import { Link } from "i18n/routing"
import { Mail, Menu, Phone, User, X } from "lucide-react"
import { usePathname } from "next/navigation"
import LogoSvg from "public/assets/svg/common/LogoSvg"

import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from "@/components/ui/drawer"
import { cn, maxRoleHelper } from "@/lib/utils"
import { cva } from "class-variance-authority"
import { useState } from "react"

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"

import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form"

import { Input, InputPassword } from "@/components/ui/input"

import { useSignIn } from "@/api/app/auth/login/queries"
import { Loader } from "@/components/ui/loader"
import { ROLE_USER_CONSTANT } from "@/constants/ConstantRole"
import { ConstantSessionStatus } from "@/constants/ConstantSessionStatus"
import { defaultValuesLoginSchema, useLoginSchema } from "@/schemas/user.schema"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import { signOut, useSession } from "next-auth/react"
import { useTranslations } from "next-intl"

const navVariants = cva("w-full grid bg-muted py-4", {
	variants: {
		variant: {
			primary:
				"bg-primary text-background dark:bg-primary dark:text-foreground",
		},
		defaultVariants: {
			variant: "default",
		},
	},
})

const DialogLogin = () => {
	const t = useTranslations()

	const { data: session, status: sessionStatus, update } = useSession()

	const { isPending, mutateAsync } = useSignIn()

	const loginSchema = useLoginSchema()

	const form = useForm({
		resolver: zodResolver(loginSchema),
		defaultValues: defaultValuesLoginSchema,
	})

	const onSubmit = async (data) => {
		const res = await mutateAsync({ body: data })

		if (res.status === 200) {
			await update()
		} else {
			form.setError("username", {
				message:
					"L’utilisateur n’existe pas ou le mot de passe est incorrect. En cas d’échecs répétés, vos prochaines tentatives pourront être soumises à un délai d’attente.",
			})
		}
	}

	return (
		<DialogContent className="max-h-screen overflow-y-auto">
			{sessionStatus === ConstantSessionStatus.LOADING ? (
				<>
					<DialogHeader>
						<LogoSvg className="h-[42px]" />
						<DialogTitle className="pt-4 text-xl">
							{t("login_form.title")}
						</DialogTitle>
						<VisuallyHidden>
							<DialogDescription className="flex flex-col items-center gap-8">
								{t("login_form.subtitle_bo")}
							</DialogDescription>
						</VisuallyHidden>
					</DialogHeader>
					<div className="flex items-center justify-center">
						<Loader variant="large" />
					</div>
				</>
			) : sessionStatus === ConstantSessionStatus.UNAUTHENTICATED &&
			  !session ? (
				<>
					<DialogHeader>
						<LogoSvg className="h-[42px]" />
						<DialogTitle className="pt-4 text-xl">
							{t("login_form.title")}
						</DialogTitle>
						<DialogDescription className="flex flex-col items-center justify-center gap-8">
							{t("login_form.subtitle")}
						</DialogDescription>
					</DialogHeader>
					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(onSubmit)}
							className="flex flex-col w-full gap-8"
						>
							<FormField
								control={form.control}
								name="username"
								render={({ field }) => (
									<FormItem>
										<FormLabel
											className="text-left"
											variant="required"
										>
											{t("common.label.email")}
										</FormLabel>
										<FormControl>
											<Input
												placeholder={t(
													"common.placeholder.email"
												)}
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel
											className="text-left"
											variant="required"
										>
											{t("common.label.password")}
										</FormLabel>
										<FormControl>
											<InputPassword
												placeholder={t(
													"common.placeholder.password"
												)}
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<div className="flex flex-col gap-2">
								<Link
									href={"/forgot-password"}
									className="underline text-secondary"
								>
									{t("login_form.forgot_password")}
								</Link>
								<Link
									href={"/register"}
									className="underline text-secondary"
								>
									{t("login_form.register")}
								</Link>
							</div>
							<LoadingButton
								loading={isPending}
								disabled={isPending}
								type="submit"
								variant="secondary"
								className="self-end"
							>
								{t("common.button.login")}
							</LoadingButton>
						</form>
					</Form>
				</>
			) : (
				<>
					<DialogHeader>
						<LogoSvg className="h-[42px]" />
						<DialogTitle className="pt-4 text-xl">
							{`${t("login_form.title")}, ${
								session?.user?.first_name
							}`}
						</DialogTitle>
						<DialogDescription className="flex flex-col items-center gap-8 text-left">
							{t("profile_modal.content")}
						</DialogDescription>
					</DialogHeader>

					<Button variant="secondary" className="self-end" asChild>
						<Link href={"/app"}>
							{t("common.button.online_report")}
						</Link>
					</Button>

					{maxRoleHelper(session?.user?.roles) !==
					ROLE_USER_CONSTANT ? (
						<Button variant="outline" asChild className="self-end">
							<Link href={`/city`}>
								{`Back-office de ${session.user?.city?.name}`}
							</Link>
						</Button>
					) : null}

					<p
						onClick={() => signOut({ redirect: false })}
						className="flex items-center justify-center underline cursor-pointer text-secondary"
					>
						{t("common.button.logout")}
					</p>
				</>
			)}
		</DialogContent>
	)
}

export const Nav = ({ variant }) => {
	const t = useTranslations()

	const { data: session, status: sessionStatus } = useSession()

	const pathname = usePathname()
	const [isDrawerOpen, setIsSrawerOpen] = useState(false)

	return (
		<header id="navigation" className={navVariants({ variant })}>
			<nav
				className={
					"flex flew-row items-center justify-between px-4 sm:px-6 md:px-8 lg:hidden"
				}
			>
				<Link href={"/"}>
					<LogoSvg className="h-[38px]" />
				</Link>
				<div className="flex items-center gap-2">
					<Dialog>
						<DialogTrigger asChild>
							<Button
								variant="outline"
								size="icon"
								className={cn(
									variant === "primary"
										? "bg-primary dark:bg-background"
										: null
								)}
							>
								<User />
							</Button>
						</DialogTrigger>
						<DialogLogin />
					</Dialog>
					<Drawer direction="right" open={isDrawerOpen}>
						<DrawerTrigger asChild>
							<Button
								variant="outline"
								size="icon"
								className={cn(
									variant === "primary"
										? "bg-primary dark:bg-background"
										: null
								)}
								onClick={() => setIsSrawerOpen(!isDrawerOpen)}
							>
								<Menu strokeWidth={2} />
							</Button>
						</DrawerTrigger>
						<DrawerContent className="bg-primary border-primary">
							<DrawerHeader className="flex items-center justify-between">
								<VisuallyHidden>
									<DrawerTitle>{`thelma`}</DrawerTitle>
								</VisuallyHidden>
								<LogoSvg
									className="h-[32px]"
									classNameText="text-white"
								/>
								<div className="flex items-center gap-2">
									<Link href={"/login"}>
										<Button
											variant="outline"
											className="bg-primary text-background dark:text-foreground dark:bg-background"
											size="icon"
											onClick={() =>
												setIsSrawerOpen(!isDrawerOpen)
											}
										>
											<User />
										</Button>
									</Link>
									<DrawerClose asChild>
										<Button
											variant="outline"
											className="bg-primary text-background dark:text-foreground dark:bg-background"
											size="icon"
											onClick={() =>
												setIsSrawerOpen(!isDrawerOpen)
											}
										>
											<X />
										</Button>
									</DrawerClose>
								</div>
								<VisuallyHidden>
									<DrawerDescription>
										Mobile Sidebar
									</DrawerDescription>
								</VisuallyHidden>
							</DrawerHeader>

							<div className="flex flex-col justify-center h-full text-base text-background dark:text-foreground">
								<div className="flex flex-col items-center gap-6 px-4">
									{NAV_LIST.map((link, idx) => {
										const isActive =
											pathname === link.pathname
										return (
											<Link
												href={link.pathname}
												key={idx}
												className={cn(
													isActive ? "underline" : ""
												)}
											>
												{link.name_fr}
											</Link>
										)
									})}
								</div>
								<hr className="mx-6 my-12 border-white/30"></hr>
								<div className="flex flex-col items-center gap-6 px-4">
									{sessionStatus ===
									ConstantSessionStatus.AUTHENTICATED ? (
										<Link href={"/app"}>
											<LoadingButton
												loading={
													sessionStatus ===
													ConstantSessionStatus.LOADING
												}
												onClick={() => {
													setIsSrawerOpen(
														!isDrawerOpen
													)
												}}
												variant="secondary"
											>
												{`${t(
													"common.button.online_report"
												)}`}
											</LoadingButton>
										</Link>
									) : (
										<Dialog>
											<DialogTrigger asChild>
												<Button
													variant="secondary"
													className="self-center"
												>
													{t(
														"common.button.online_report"
													)}
												</Button>
											</DialogTrigger>
											<DialogLogin />
										</Dialog>
									)}
								</div>
								<hr className="mx-6 my-12 border-white/30"></hr>
								<div className="flex flex-col items-center gap-6 px-4">
									<Link
										href="mail:support@thelma.app"
										className="flex items-center gap-4 flew-row"
									>
										<Mail strokeWidth={1.2} />
										{`support@thelma.app`}
									</Link>

									<Link
										href="tel:+33158393999"
										className="flex items-center gap-4 flew-row"
									>
										<Phone strokeWidth={1.2} />
										{`+33 (1) 58 39 39 99`}
									</Link>
								</div>
							</div>
						</DrawerContent>
					</Drawer>
				</div>
			</nav>
			<nav className="items-center justify-between hidden w-full px-8 max-w-screen-2xl place-self-center lg:flex">
				<div className="flex items-center gap-10 flew-row xl:gap-16">
					<Link href={"/"}>
						<LogoSvg className={"h-[40px]"} variant={variant} />
					</Link>
					<div className="flex items-center gap-8 flew-row">
						{NAV_LIST.map((link, idx) => {
							const isActive = pathname === link.pathname

							return (
								<Link
									href={link.pathname}
									key={idx}
									className={cn(
										"text-sm text-semibold",
										isActive ? "underline" : "",
										variant === "dark"
											? "text-background"
											: ""
									)}
								>
									{link.name_fr}
								</Link>
							)
						})}
					</div>
				</div>
				<div className="flex items-center gap-4 flew-row lg:gap-8">
					<Link
						href="tel:+33158393999"
						className="flex items-center gap-2 flew-row"
					>
						<Phone strokeWidth={1.2} />
						{`+33 (1) 58 39 39 99`}
					</Link>
					<Dialog>
						<DialogTrigger asChild>
							<Button
								variant="outline"
								size="icon"
								className={cn(
									variant === "primary"
										? "bg-primary dark:bg-background"
										: null
								)}
							>
								<User />
							</Button>
						</DialogTrigger>
						<DialogLogin />
					</Dialog>
				</div>
			</nav>
		</header>
	)
}
